export function getOutlierRange<T>(
  data: T[],
  getValue: (p: T) => number,
  quartileMultipler = 1.5,
): { lowerBound: number; upperBound: number } | null {
  // Sort the numbers
  const sortedNumbers = [...data].sort((a, b) => getValue(a) - getValue(b))

  if (sortedNumbers.length === 0) {
    return null
  }

  if (sortedNumbers.length < 4) {
    // Not enough data points to calculate IQR
    const buffer = 0.1 // Small buffer space so points are not on the edges
    return {
      lowerBound: getValue(sortedNumbers[0]) * (1 - buffer),
      upperBound:
        getValue(sortedNumbers.at(-1) ?? sortedNumbers[0]) * (1 + buffer),
    }
  }

  // Calculate Q1 (25th percentile)
  const q1 = calculatePercentile(sortedNumbers, getValue, 25)

  // Calculate Q3 (75th percentile)
  const q3 = calculatePercentile(sortedNumbers, getValue, 75)

  // Calculate IQR
  const iqr = q3 - q1

  // Determine the bounds for outliers
  const lowerBound = q1 - quartileMultipler * iqr
  const upperBound = q3 + quartileMultipler * iqr

  // Return the outlier bounds
  return { lowerBound, upperBound }
}

function calculatePercentile<T>(
  sortedData: T[],
  getValue: (p: T) => number,
  percentile: number,
): number {
  const index = (percentile / 100) * (sortedData.length - 1)
  const lowerIndex = Math.floor(index)
  const upperIndex = lowerIndex + 1

  if (upperIndex >= sortedData.length) {
    return getValue(sortedData[lowerIndex])
  }
  const fraction = index - lowerIndex
  return (
    getValue(sortedData[lowerIndex]) +
    fraction *
      (getValue(sortedData[upperIndex]) - getValue(sortedData[lowerIndex]))
  )
}

export function generateMovingAverage(
  points: { id: string; price: number }[],
  windowSize: number,
): Record<string, number> {
  if (points.length === 0 || windowSize <= 0) {
    return {}
  }

  const result: Record<string, number> = {}
  let slidingSum = 0.0

  for (let i = 0; i < points.length; i++) {
    slidingSum += points[i].price

    if (i < windowSize - 1) {
      // If the window is not full, calculate the average based on the elements so far
      result[points[i].id] = slidingSum / (i + 1) // Use the current index to avoid NaN
    } else {
      // Once the window is full, slide the window and calculate the average
      if (i >= windowSize) {
        slidingSum -= points[i - windowSize].price
      }
      result[points[i].id] = slidingSum / windowSize
    }
  }

  return result
}

export function isTokenStatsPage(pathname: string, kind?: "nfts" | "tokens") {
  if (pathname.includes("watchlist") && kind === "tokens") {
    return true
  }
  return pathname.includes("tokens")
}

export function isTokenWatchlistPage(
  pathname: string,
  kind?: "nfts" | "tokens",
) {
  return pathname.includes("watchlist") && kind === "tokens"
}
